import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, FilterX, Download, Banknote, X, ListTodo } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";
// The external PPD format is shared with Manual Products admin. The Jobalots
// import file is built server-side since 2026-10-31 (BE
// services/jobalotsExport.js, queued via POST /wipe/jobalots-export).
import {
    PPD_HEADER, PPD_CONSTANTS,
    ppdNum, downloadCsv, downloadXlsx,
} from "./helpers/exportFormats";

export { ppdNum };

const PAGE_SIZE = 25;
// BE caps page_size at 200 — the export pages through in these chunks.
const EXPORT_CHUNK = 200;

// Outcomes offered in the filter, in workflow order. cover_relabel is
// intentionally omitted — those rows are hidden from this view entirely.
const OUTCOMES = [
    "auto_list",
    "auto_dispose",
    "data_wipe_relabel",
    "relabel",
    "relabel_dispose",
    "cover_relabel",
    "cover_unmanifested",
    "box_blue",
    "box_pink",
] as const;
const OUTCOME_SET = new Set<string>(OUTCOMES);

export interface WipeRow {
    id: number;
    created_at: string;
    warehouse: string | null;
    relabel_barcode: string | null;
    scanned_barcode: string | null;
    jli_pkg_id: string | null;
    asin: string | null;
    item_desc: string | null;
    data_sensitive: boolean | null;
    outcome: string | null;
    unit_recovery: number | string | null;
    item_pkg_weight: number | string | null;
    // Not shown in the table; used by the PPD export (RRP / Sell Price).
    supplier_unit_cost: number | string | null;
    // The SKU the unit is sold under, decided by the BE (wipe.domain
    // WIPE_SKU_SQL): the new label, or — for a label-less Pink unit
    // (auto_list) — the Amazon LPN it keeps wearing. Null for whole-box rows
    // and box-code cartons. Older callers / tests may omit it.
    sku?: string | null;
    // Jobalots' verdict from the stock pushes (BE listingStatus.js): true =
    // listed, false = rejected (not on Jobalots), null = never pushed.
    jobalots_listed?: boolean | null;
    jobalots_checked_at?: string | null;
}

// created_at → DD/MM/YYYY (date only).
const formatDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Unit cost → £X.XX; blank cell for missing values.
const formatCost = (v: number | string | null) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    return Number.isFinite(n) ? `£${n.toFixed(2)}` : "—";
};

// Weight in grams → whole-number string; blank cell for missing values.
const formatWeight = (v: number | string | null) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.round(n)) : "—";
};

const cell = (v: string | null) => (v === null || v === undefined || v === "" ? "—" : v);

// PPD_HEADER / PPD_CONSTANTS and the CSV / XLSX download helpers live in
// helpers/exportFormats.ts (shared with Manual Products admin since
// 2026-10-15).

// Identifier-style columns forced to Text on export so Excel keeps long
// barcode / id digit strings verbatim instead of mangling them to
// scientific notation. Keyed by header position (see exportTable).
const EXPORT_TEXT_COLUMNS = new Set<number>([0, 3, 4, 5, 6]); // id, new/old SKU, pkg id, asin

// Weight / recovery borrowed from the newest manifest line for an ASIN, for
// rows that reached Wipe via the Products fallback (which stores neither).
// Served by POST /wipe/manifest-defaults; ASINs with no manifest line are
// absent from the map.
export interface ManifestDefaults {
    itemPkgWeight: number | null;
    unitRecovery: number | null;
}

// BE caps /wipe/manifest-defaults at this many ASINs per request.
const MANIFEST_DEFAULTS_CHUNK = 500;

// Unit Cost / Weight cells for the TABLE export — delegate to the PPD mapping
// so both exports always carry identical values: the row's own value, else the
// newest manifest line's, else the ops-spec defaults (0 cost / 980 g).
export const exportCost = (r: WipeRow, manifest?: ManifestDefaults): string | number =>
    ppdValue("Product Cost", r, manifest);
export const exportWeight = (r: WipeRow, manifest?: ManifestDefaults): string | number =>
    ppdValue("Product Weight (grams)", r, manifest);

// One PPD cell: constant columns come from PPD_CONSTANTS; the per-product
// columns map from the wipe row per the operations spec (2026-07-08).
// Rows without a weight / recovery of their own (Products-collection fallback)
// borrow the newest manifest line's values for the same ASIN; only when the
// ASIN is on no manifest at all do the ops-spec defaults (980 g / 0) apply.
export const ppdValue = (header: string, r: WipeRow, manifest?: ManifestDefaults): string | number => {
    if (Object.prototype.hasOwnProperty.call(PPD_CONSTANTS, header)) {
        return PPD_CONSTANTS[header];
    }
    switch (header) {
        case "EAN":
        case "EAN_channel(MasterKey)":
        case "EAN_owner":
            return exportSku(r);
        case "Product Title":
            return r.item_desc ?? "";
        case "Product Description":
            return "";
        case "Product Weight (grams)": {
            const w = ppdNum(r.item_pkg_weight, true);
            if (w !== "") return w;
            const fromManifest = ppdNum(manifest?.itemPkgWeight, true);
            return fromManifest === "" ? 980 : fromManifest;
        }
        case "Bookon Cost":
        case "Product Cost": {
            const c = ppdNum(r.unit_recovery);
            if (c !== "") return c;
            const fromManifest = ppdNum(manifest?.unitRecovery);
            return fromManifest === "" ? 0 : fromManifest;
        }
        case "Product RRP":
        case "Product Sell Price":
            return ppdNum(r.supplier_unit_cost);
        default:
            return "";
    }
};

// The table XLSX mirror skips rows without a New SKU — a wipe product that
// was never assigned a relabel barcode has nothing in that column.
export const hasNewSku = (r: WipeRow): boolean => Boolean(r.relabel_barcode?.trim());

// The SKU a row is sold under: the BE's `sku` (new label, else the LPN of a
// label-less Pink unit — Alex 2026-11-02, LPNWE296519399), falling back to
// the label for rows fetched without it. "" when the row has none.
export const exportSku = (r: WipeRow): string => (r.sku ?? r.relabel_barcode ?? "").trim();

// Rows that reach the PPD / Jobalots exports and carry a Jobalots verdict:
// anything with a SKU except a cover label (cover_relabel for a whole
// data-bearing box, cover_unmanifested for an unknown LPN) — those have a
// barcode but no product behind them, so they can't be listed or priced. The
// table itself (and its XLSX mirror) shows every recorded scan; whole-box rows
// and box-code auto_list cartons have no per-unit SKU and drop out.
const COVER_OUTCOMES = new Set(["cover_relabel", "cover_unmanifested"]);
export const isExportable = (r: WipeRow): boolean => exportSku(r) !== "" && !COVER_OUTCOMES.has(r.outcome ?? "");

// Pseudo-warehouse pills that expand into a list of real warehouses when the
// BE call goes out (same shape as the Inventory / Totes admin tables). "UK"
// covers Pembroke + Newport, so the list AND every export (Table / PPD /
// Jobalots) carry both sites. Values are prefixed `group:` so they can't
// collide with a real warehouse name.
export const WAREHOUSE_GROUPS: Record<string, string[]> = {
    "group:UK": ["Pembroke", "Newport"],
};
export const expandWarehouseFilter = (raw: string): string[] => {
    if (!raw) return [];
    return WAREHOUSE_GROUPS[raw] ?? [raw];
};

// Read-only view of the wipe_products table for the Warehouse admin area.
// Server-paginated with a free-text search across the identifier / description
// columns. Mirrors the RelabelAdmin list/search/pagination pattern.
const WipeAdmin = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<WipeRow[]>([]);
    const [total, setTotal] = useState(0);
    // Free-text search — BE matches across barcode / pkg id / asin / title /
    // warehouse. Commits on Enter / blur so a fast typist doesn't hit the BE
    // per keystroke. `?search=` URL param lets AdminSearch deep-link in.
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    // "" = all. Date bounds are YYYY-MM-DD (native date inputs), inclusive.
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    // Without cross_warehouse the table is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [outcomeFilter, setOutcomeFilter] = useState("");
    // "" = all, "yes" = listed on Jobalots, "no" = confirmed rejected by Jobalots on its last stock push (never-pushed SKUs are in neither).
    const [listedFilter, setListedFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => { setPage(0); }, [searchFilter, warehouseFilter, outcomeFilter, listedFilter, fromDate, toDate]);

    // Warehouse options from the shared settings list (same source the
    // Inventory admin uses), so the dropdown covers every warehouse.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).filter(Boolean).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    // Active-filter query params, shared by the list load and the export so
    // the export always matches exactly what the table is showing.
    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (searchFilter.trim()) params.set("q", searchFilter.trim());
        // A group pill becomes a comma list ("Pembroke,Newport"); /wipe/list
        // matches any of them, so the exports built from these params cover
        // every warehouse in the group.
        const expanded = expandWarehouseFilter(warehouseFilter);
        if (expanded.length > 0) params.set("warehouse", expanded.join(","));
        if (outcomeFilter) params.set("outcome", outcomeFilter);
        if (listedFilter) params.set("listed", listedFilter);
        if (fromDate) params.set("from", fromDate);
        if (toDate) params.set("to", toDate);
        return params;
    }, [searchFilter, warehouseFilter, outcomeFilter, listedFilter, fromDate, toDate]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/wipe/list?${params}`, {});
            if (res.ok) {
                const data = await res.json();
                setRows(Array.isArray(data.wipe) ? data.wipe : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "wipe-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildFilterParams, page, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const hasActiveFilters = Boolean(
        searchFilter.trim() || pickedWarehouse || outcomeFilter || listedFilter || fromDate || toDate,
    );
    const clearFilters = () => {
        setSearchFilter("");
        setSearchInput("");
        setWarehouseFilter("");
        setOutcomeFilter("");
        setListedFilter("");
        setFromDate("");
        setToDate("");
    };

    // The Jobalots column: the verdict of the last stock push for the SKU.
    // Only rows the Jobalots export carries (a label or a Pink unit's LPN,
    // not a cover label) can be listed, so the others show nothing.
    const jobalotsCell = (r: WipeRow) => {
        if (!isExportable(r)) return <td />;
        const label = r.jobalots_listed === true ? t("wipe_jobalots_listed")
            : r.jobalots_listed === false ? t("wipe_jobalots_not_listed")
                : t("wipe_jobalots_not_checked");
        const tone = r.jobalots_listed === true ? "wipe-jobalots--listed" : r.jobalots_listed === false ? "wipe-jobalots--not-listed" : "wipe-jobalots--unknown";
        const title = r.jobalots_checked_at ? t("wipe_jobalots_checked_at", { date: formatDate(r.jobalots_checked_at) }) : undefined;
        return <td className={`wipe-jobalots ${tone}`} title={title}>{label}</td>;
    };

    // Friendly label for an outcome enum; falls back to the raw value. The two
    // whole-box outcomes share the "Pink"/"Blue" pallet labels with the single-
    // item outcomes, so prefix them with "Box" here to keep every label unique.
    const outcomeLabel = (o: string | null) => {
        if (!o) return "—";
        if (!OUTCOME_SET.has(o)) return o;
        const base = t(`wipe_outcome_${o}`);
        return o === "box_blue" || o === "box_pink"
            ? t("wipe_outcome_box_prefixed", { label: base })
            : base;
    };

    // Warehouse dropdown: group pills (UK = Pembroke + Newport) first, only
    // when at least one member is in scope, then the individual warehouses.
    // Without cross_warehouse the select is pinned to the person's own site.
    const warehouseOptions = (() => {
        if (!warehouseScope.canCross) return warehouses.map(w => ({ value: w, label: w }));
        const groupOptions = Object.entries(WAREHOUSE_GROUPS)
            .filter(([, members]) => members.some(m => warehouses.includes(m)))
            .map(([groupKey]) => ({ value: groupKey, label: groupKey.replace(/^group:/, "") }));
        return [...groupOptions, ...warehouses.map(w => ({ value: w, label: w }))];
    })();

    // Dropdown options sorted alphabetically by their (unique) label.
    const outcomeOptions = OUTCOMES
        .map(o => ({ value: o, label: outcomeLabel(o) }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Collect EVERY row matching the current filters (not just the page on
    // screen). Pages through the BE (which caps page_size at 200) until the
    // reported total is collected. Shared by both exports.
    const fetchAllFiltered = async (): Promise<WipeRow[]> => {
        const all: WipeRow[] = [];
        let exportPage = 0;
        let expected = Infinity;
        do {
            const params = buildFilterParams();
            params.set("page", String(exportPage));
            params.set("page_size", String(EXPORT_CHUNK));
            const res = await apiFetch(`/wipe/list?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const batch: WipeRow[] = Array.isArray(data.wipe) ? data.wipe : [];
            all.push(...batch);
            expected = typeof data.total === "number" ? data.total : all.length;
            exportPage += 1;
            // Guard against a short/empty batch so we never loop forever.
            if (batch.length === 0) break;
        } while (all.length < expected);
        return all;
    };

    // ---- Exchange rates (HMRC monthly, behind the Wipe → products GBP conversion) ----
    // Admin › Warehouse › Wipe can see the stored months, refetch a month from
    // HMRC, or type an override (which a later HMRC refresh leaves alone).
    interface RateRow { month: string; currency: string; units_per_gbp: string | number; source: string; fetched_at: string; updated_by: string | null }
    const [ratesOpen, setRatesOpen] = useState(false);
    const [rates, setRates] = useState<RateRow[]>([]);
    const [ratesLoading, setRatesLoading] = useState(false);
    const [rateEdits, setRateEdits] = useState<Record<string, string>>({});
    const RATE_CURRENCIES = "EUR,PLN";

    const loadRates = useCallback(async () => {
        setRatesLoading(true);
        try {
            const res = await apiFetch(`/wipe/exchange-rates?months=6&currencies=${RATE_CURRENCIES}`);
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            setRates(Array.isArray(data.rates) ? data.rates : []);
            setRateEdits({});
        } catch {
            toast.error(t("server_error"), { toastId: "wipe-rates-load" });
        } finally {
            setRatesLoading(false);
        }
    }, [t]);

    const openRates = () => { setRatesOpen(true); loadRates(); };

    const refreshRatesFromHmrc = async () => {
        setRatesLoading(true);
        try {
            const now = new Date();
            const res = await apiFetch(`/wipe/exchange-rates/refresh`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 }),
            });
            if (!res.ok) throw new Error(String(res.status));
            toast.success(t("wipe_rates_refreshed"), { toastId: "wipe-rates-refreshed" });
            await loadRates();
        } catch {
            toast.error(t("wipe_rates_refresh_failed"), { toastId: "wipe-rates-refresh-err" });
            setRatesLoading(false);
        }
    };

    const saveRateOverride = async (row: RateRow) => {
        const key = `${row.month}|${row.currency}`;
        const value = Number(rateEdits[key]);
        if (!Number.isFinite(value) || value <= 0) { toast.error(t("wipe_rates_invalid"), { toastId: "wipe-rates-invalid" }); return; }
        setRatesLoading(true);
        try {
            const res = await apiFetch(`/wipe/exchange-rates`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ month: row.month, currency: row.currency, unitsPerGbp: value }),
            });
            if (!res.ok) throw new Error(String(res.status));
            toast.success(t("wipe_rates_saved"), { toastId: "wipe-rates-saved" });
            await loadRates();
        } catch {
            toast.error(t("server_error"), { toastId: "wipe-rates-save-err" });
            setRatesLoading(false);
        }
    };

    // Table export — one XLSX with the same columns the table shows.
    const exportTable = async () => {
        setDownloading(true);
        try {
            const all = (await fetchAllFiltered()).filter(hasNewSku);
            const manifestDefaults = await fetchManifestDefaults(all);

            const header = [
                t("wipe_col_id"),
                t("wipe_col_date"),
                t("wipe_col_warehouse"),
                t("wipe_col_new_sku"),
                t("wipe_col_old_sku"),
                t("wipe_col_jli_pkg_id"),
                t("wipe_col_asin"),
                t("wipe_col_title"),
                t("wipe_col_data_sensitive"),
                t("wipe_col_outcome"),
                t("wipe_col_unit_cost"),
                t("wipe_col_weight"),
            ];
            // Text columns mirror the table; Unit Cost / Weight stay numeric
            // so the spreadsheet can sum them. Rows without their own values
            // borrow the newest manifest line's (see exportCost/exportWeight).
            const out: (string | number)[][] = all.map(r => {
                const asin = r.asin?.trim();
                const fromManifest = asin ? manifestDefaults[asin] : undefined;
                return [
                    String(r.id),
                    formatDate(r.created_at),
                    r.warehouse ?? "",
                    r.relabel_barcode ?? "",
                    r.scanned_barcode ?? "",
                    r.jli_pkg_id ?? "",
                    r.asin ?? "",
                    r.item_desc ?? "",
                    r.data_sensitive ? t("wipe_yes") : t("wipe_no"),
                    r.outcome ? outcomeLabel(r.outcome) : "",
                    exportCost(r, fromManifest),
                    exportWeight(r, fromManifest),
                ];
            });
            await downloadXlsx("wipe-products.xlsx", header, out, EXPORT_TEXT_COLUMNS, "Wipe");
        } catch {
            toast.error(t("server_error"), { toastId: "wipe-export-err" });
        } finally {
            setDownloading(false);
        }
    };

    // Manifest fallback for the PPD export: collect the ASINs of rows missing
    // a weight or recovery, and fetch the newest manifest line's values for
    // each (chunked to the BE's per-request cap). A failed lookup throws so
    // the export errors visibly instead of silently writing the defaults.
    const fetchManifestDefaults = async (rows: WipeRow[]): Promise<Record<string, ManifestDefaults>> => {
        const needsFallback = (r: WipeRow) =>
            ppdNum(r.item_pkg_weight) === "" || ppdNum(r.unit_recovery) === "";
        // Trim to match the BE, which trims incoming ASINs and keys the
        // response by the trimmed value.
        const asins = [...new Set(
            rows.filter(needsFallback).map(r => r.asin?.trim()).filter((a): a is string => !!a),
        )];
        const defaults: Record<string, ManifestDefaults> = {};
        for (let i = 0; i < asins.length; i += MANIFEST_DEFAULTS_CHUNK) {
            const res = await apiFetch("/wipe/manifest-defaults", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ asins: asins.slice(i, i + MANIFEST_DEFAULTS_CHUNK) }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            Object.assign(defaults, data.defaults ?? {});
        }
        return defaults;
    };

    // PPD export — CSV in the external PPD product-import format, one row per
    // filtered wipe product (see ppdValue for the column mapping).
    const ppdExport = async () => {
        setDownloading(true);
        try {
            const all = (await fetchAllFiltered()).filter(isExportable);
            const manifestDefaults = await fetchManifestDefaults(all);
            const out = all.map(r => {
                const asin = r.asin?.trim();
                return PPD_HEADER.map(h => ppdValue(h, r, asin ? manifestDefaults[asin] : undefined));
            });
            downloadCsv("ppd-export.csv", [[...PPD_HEADER], ...out]);
        } catch {
            toast.error(t("server_error"), { toastId: "wipe-ppd-err" });
        } finally {
            setDownloading(false);
        }
    };

    // Jobalots export — queued server-side with the current filters (same
    // query string as the list) and built by the jobs runner, which fills
    // the multi-language title column; the file appears under Admin ›
    // Warehouse › Reports › Queued exports.
    const jobalotsExport = async () => {
        setDownloading(true);
        try {
            const res = await apiFetch(`/wipe/jobalots-export?${buildFilterParams()}`, { method: "POST" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success(t("wipe_jobalots_queued"), { toastId: "wipe-jobalots-queued" });
        } catch {
            toast.error(t("wipe_jobalots_queue_failed"), { toastId: "wipe-jobalots-err" });
        } finally {
            setDownloading(false);
        }
    };

    // Sync Jobalots — re-push stock for every SKU in the current filters
    // whose Jobalots status is not Listed (Not listed or Not checked). The
    // production outbox sends the pushes over the next minutes and each
    // reply refreshes the Jobalots column; Refresh to see it.
    const jobalotsResync = async () => {
        setDownloading(true);
        try {
            const res = await apiFetch(`/wipe/jobalots-resync?${buildFilterParams()}`, { method: "POST" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const count = Number(data?.total) || 0;
            if (count === 0) toast.info(t("wipe_jobalots_sync_none"), { toastId: "wipe-jobalots-sync" });
            else toast.success(t("wipe_jobalots_sync_started", { count: count.toLocaleString("en-GB") }), { toastId: "wipe-jobalots-sync" });
        } catch {
            toast.error(t("wipe_jobalots_sync_failed"), { toastId: "wipe-jobalots-sync-err" });
        } finally {
            setDownloading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    return (
        <div className="inventory-admin">
            {ratesOpen && (
                <div className="wipe-rates-overlay" role="dialog" aria-modal="true" aria-label={t("wipe_rates_title")}>
                    <div className="wipe-rates-modal">
                        <div className="wipe-rates-head">
                            <h3>{t("wipe_rates_title")}</h3>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRatesOpen(false)} aria-label={t("close")}><X size={16} /></button>
                        </div>
                        <p className="wipe-rates-hint">{t("wipe_rates_hint")}</p>
                        <div className="wipe-rates-actions">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={refreshRatesFromHmrc} disabled={ratesLoading}>
                                <RefreshCw size={14} className={ratesLoading ? "spin" : ""} /> {t("wipe_rates_refresh")}
                            </button>
                        </div>
                        <table className="wipe-rates-table">
                            <thead>
                                <tr><th>{t("wipe_rates_col_month")}</th><th>{t("wipe_rates_col_currency")}</th><th>{t("wipe_rates_col_rate")}</th><th>{t("wipe_rates_col_source")}</th><th></th></tr>
                            </thead>
                            <tbody>
                                {rates.length === 0 && !ratesLoading && (
                                    <tr><td colSpan={5} className="wipe-rates-empty">{t("wipe_rates_empty")}</td></tr>
                                )}
                                {rates.map((r) => {
                                    const key = `${r.month}|${r.currency}`;
                                    const edited = rateEdits[key];
                                    return (
                                        <tr key={key}>
                                            <td>{r.month.slice(0, 7)}</td>
                                            <td>{r.currency}</td>
                                            <td>
                                                <input
                                                    id={`rate-${r.currency}-${r.month.slice(0, 7)}`}
                                                    type="number" step="0.0001" min="0.0001"
                                                    value={edited ?? String(Number(r.units_per_gbp))}
                                                    onChange={(e) => setRateEdits((m) => ({ ...m, [key]: e.target.value }))}
                                                    disabled={ratesLoading}
                                                />
                                            </td>
                                            <td>{r.source === "override" ? `${t("wipe_rates_source_override")}${r.updated_by ? ` (${r.updated_by})` : ""}` : "HMRC"}</td>
                                            <td>
                                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => saveRateOverride(r)}
                                                    disabled={ratesLoading || edited === undefined || edited === String(Number(r.units_per_gbp))}>
                                                    {t("save")}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="totes-admin-actions">
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={exportTable}
                    disabled={loading || downloading || total === 0}
                >
                    <Download size={16} /> {downloading ? t("submitting") : t("wipe_export_table")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={ppdExport}
                    disabled={loading || downloading || total === 0}
                >
                    <Download size={16} /> {t("wipe_ppd_export")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={jobalotsExport}
                    disabled={loading || downloading || total === 0}
                >
                    <ListTodo size={16} /> {t("wipe_jobalots_queue")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={jobalotsResync}
                    disabled={loading || downloading || total === 0}
                    title={t("wipe_jobalots_sync_hint")}
                >
                    <RefreshCw size={16} /> {t("wipe_jobalots_sync")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={openRates}
                    disabled={ratesLoading}
                >
                    <Banknote size={16} /> {t("wipe_rates_button")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    style={{ marginLeft: "auto" }}
                >
                    <FilterX size={14} /> {t("wipe_clear_filters")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={refresh}
                    disabled={loading}
                >
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
            </div>

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("wipe_filter_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== searchFilter.trim()) {
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                />
                <select
                    className="wh-admin-filter-select"
                    value={warehouseFilter}
                    onChange={e => setWarehouseFilter(e.target.value)}
                    disabled={!warehouseScope.canCross}
                    aria-label={t("wipe_col_warehouse")}
                >
                    {warehouseScope.canCross && <option value="">{t("wipe_filter_all_warehouses")}</option>}
                    {warehouseOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <select
                    className="wh-admin-filter-select"
                    value={outcomeFilter}
                    onChange={e => setOutcomeFilter(e.target.value)}
                    aria-label={t("wipe_col_outcome")}
                >
                    <option value="">{t("wipe_filter_all_outcomes")}</option>
                    {outcomeOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <select
                    className="wh-admin-filter-select"
                    value={listedFilter}
                    onChange={e => setListedFilter(e.target.value)}
                    aria-label={t("wipe_col_jobalots")}
                >
                    <option value="">{t("wipe_filter_all_listing")}</option>
                    <option value="yes">{t("wipe_filter_listed")}</option>
                    <option value="no">{t("wipe_filter_not_listed")}</option>
                </select>
                <div className="wipe-date-filters">
                    <label className="wipe-date-field">
                        <span className="wipe-date-label">{t("wipe_filter_from")}</span>
                        <input
                            className="wh-admin-filter-date"
                            type="date"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={e => setFromDate(e.target.value)}
                        />
                    </label>
                    <label className="wipe-date-field">
                        <span className="wipe-date-label">{t("wipe_filter_to")}</span>
                        <input
                            className="wh-admin-filter-date"
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={e => setToDate(e.target.value)}
                        />
                    </label>
                </div>
            </div>

            <div className="locations-table-wrap">
                <table className="locations-table wipe-admin-table">
                    <thead>
                        <tr>
                            <th className="locations-table-num">{t("wipe_col_id")}</th>
                            <th>{t("wipe_col_date")}</th>
                            <th>{t("wipe_col_warehouse")}</th>
                            <th>{t("wipe_col_new_sku")}</th>
                            <th>{t("wipe_col_old_sku")}</th>
                            <th>{t("wipe_col_jli_pkg_id")}</th>
                            <th>{t("wipe_col_asin")}</th>
                            <th>{t("wipe_col_title")}</th>
                            <th>{t("wipe_col_data_sensitive")}</th>
                            <th>{t("wipe_col_outcome")}</th>
                            <th>{t("wipe_col_jobalots")}</th>
                            <th className="locations-table-num">{t("wipe_col_unit_cost")}</th>
                            <th className="locations-table-num">{t("wipe_col_weight")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id}>
                                <td className="locations-table-num">{r.id}</td>
                                <td>{formatDate(r.created_at)}</td>
                                <td>{cell(r.warehouse)}</td>
                                <td title={r.relabel_barcode ?? undefined}>{cell(r.relabel_barcode)}</td>
                                <td title={r.scanned_barcode ?? undefined}>{cell(r.scanned_barcode)}</td>
                                <td title={r.jli_pkg_id ?? undefined}>{cell(r.jli_pkg_id)}</td>
                                <td title={r.asin ?? undefined}>{cell(r.asin)}</td>
                                <td title={r.item_desc ?? undefined}>{cell(r.item_desc)}</td>
                                <td>{r.data_sensitive ? t("wipe_yes") : t("wipe_no")}</td>
                                <td title={r.outcome ?? undefined}>{outcomeLabel(r.outcome)}</td>
                                {jobalotsCell(r)}
                                <td className="locations-table-num">{formatCost(r.unit_recovery)}</td>
                                <td className="locations-table-num">{formatWeight(r.item_pkg_weight)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!loading && total === 0 && (
                <div className="locations-table-empty">{t("wipe_no_rows")}</div>
            )}

            {total > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", {
                        from: String(rangeFrom),
                        to: String(rangeTo),
                        total: String(total),
                    })}</span>
                    <div className="locations-table-pagination">
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            aria-label={t("pagination_previous")}
                        >
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", {
                                page: String(safePage + 1),
                                total: String(totalPages),
                            })}
                        </span>
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            aria-label={t("pagination_next")}
                        >
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WipeAdmin;
